/**
 * Moving around the board: wheel, Ctrl+wheel, space-drag and middle-mouse drag
 * (SRS C2).
 *
 * Touchscreen pinch and two-finger drag are decided separately at step 2.6, because
 * telling those two apart needs its own rule. A touchpad pinch arrives here as a
 * Ctrl+wheel, which is what the browser sends, so it is handled with zoom.
 */

import type { Point } from '@opal/shared';

import { typingInto } from './shortcuts.js';

/** How fast Ctrl+wheel zooms. Chosen so a normal notch is a small, controllable step. */
const ZOOM_SENSITIVITY = 0.0025;

export type WheelAction =
  | { readonly kind: 'zoom'; readonly factor: number }
  | { readonly kind: 'pan'; readonly dxPx: number; readonly dyPx: number };

/** The part of a wheel event that decides what it means. */
export interface WheelLike {
  readonly deltaX: number;
  readonly deltaY: number;
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
}

/**
 * What a wheel event means.
 *
 * Pan deltas are negated: turning the wheel away scrolls the view down, which means
 * the paper moves up under it.
 */
/** Negation that never produces -0, which would otherwise leak into stored state. */
function away(value: number): number {
  return value === 0 ? 0 : -value;
}

export function wheelAction(event: WheelLike): WheelAction {
  if (event.ctrlKey) {
    return { kind: 'zoom', factor: Math.exp(-event.deltaY * ZOOM_SENSITIVITY) };
  }
  if (event.shiftKey) {
    const sideways = event.deltaX !== 0 ? event.deltaX : event.deltaY;
    return { kind: 'pan', dxPx: away(sideways), dyPx: 0 };
  }
  return { kind: 'pan', dxPx: away(event.deltaX), dyPx: away(event.deltaY) };
}

export interface NavigationHandlers {
  /** Drag the paper by this many screen pixels. */
  readonly onPan: (dxPx: number, dyPx: number) => void;
  /** Multiply the zoom, keeping the canvas point under `anchor` where it is. */
  readonly onZoom: (factor: number, anchor: Point) => void;
}

export interface Navigation {
  readonly detach: () => void;
  /** True while the space bar is down: the pen is holding still and the hand moves. */
  readonly panMode: () => boolean;
}

/** True for a drag that pans rather than draws: middle mouse, or space held down. */
function isPanDrag(event: PointerEvent, spaceHeld: boolean): boolean {
  return event.button === 1 || (spaceHeld && event.pointerType === 'mouse');
}

/** Applies a wheel event to the view: zoom about the cursor, or pan. */
function applyWheel(
  element: HTMLElement,
  handlers: NavigationHandlers,
  event: WheelEvent,
): void {
  const action = wheelAction(event);
  if (action.kind === 'pan') {
    handlers.onPan(action.dxPx, action.dyPx);
    return;
  }
  const bounds = element.getBoundingClientRect();
  handlers.onZoom(action.factor, {
    x: event.clientX - bounds.left,
    y: event.clientY - bounds.top,
  });
}

/** Tracks the space bar, which turns the pen into a hand while it is held. */
function watchSpaceBar(element: HTMLElement): { held: () => boolean; stop: () => void } {
  let spaceHeld = false;

  const keyDown = (event: KeyboardEvent): void => {
    if (event.code !== 'Space' || event.repeat) return;
    // A space typed into a text box is a space, not a hand on the paper.
    if (typingInto(event.target)) return;
    // Space would otherwise scroll the page out from under the board.
    event.preventDefault();
    spaceHeld = true;
    element.style.cursor = 'grab';
  };

  const keyUp = (event: KeyboardEvent): void => {
    if (event.code !== 'Space') return;
    spaceHeld = false;
    element.style.cursor = '';
  };

  window.addEventListener('keydown', keyDown);
  window.addEventListener('keyup', keyUp);

  return {
    held: () => spaceHeld,
    stop() {
      window.removeEventListener('keydown', keyDown);
      window.removeEventListener('keyup', keyUp);
      element.style.cursor = '';
    },
  };
}

export function attachNavigation(
  element: HTMLElement,
  handlers: NavigationHandlers,
): Navigation {
  const space = watchSpaceBar(element);
  let panning: number | null = null;
  let last: Point | null = null;

  const wheel = (event: WheelEvent): void => {
    // Without this the browser zooms the whole page instead of the board.
    event.preventDefault();
    applyWheel(element, handlers, event);
  };

  const down = (event: PointerEvent): void => {
    if (panning !== null || !isPanDrag(event, space.held())) return;
    // Stops the browser turning a middle-click into its own scroll mode.
    event.preventDefault();
    panning = event.pointerId;
    last = { x: event.clientX, y: event.clientY };
    element.setPointerCapture(event.pointerId);
  };

  const move = (event: PointerEvent): void => {
    if (panning !== event.pointerId || !last) return;
    handlers.onPan(event.clientX - last.x, event.clientY - last.y);
    last = { x: event.clientX, y: event.clientY };
  };

  const stop = (event: PointerEvent): void => {
    if (panning !== event.pointerId) return;
    panning = null;
    last = null;
  };

  element.addEventListener('wheel', wheel, { passive: false });
  element.addEventListener('pointerdown', down);
  element.addEventListener('pointermove', move);
  element.addEventListener('pointerup', stop);
  element.addEventListener('pointercancel', stop);

  return {
    panMode: () => space.held(),
    detach() {
      element.removeEventListener('wheel', wheel);
      element.removeEventListener('pointerdown', down);
      element.removeEventListener('pointermove', move);
      element.removeEventListener('pointerup', stop);
      element.removeEventListener('pointercancel', stop);
      space.stop();
    },
  };
}
